// ============================================
// Screen Share View Component
// Layout for when someone is sharing screen
// ============================================

import React from 'react';
import {
  useHMSStore,
  useVideo,
  selectScreenShareByPeerID,
  selectPeerScreenSharing
} from '@100mslive/react-sdk';
import VideoTile from './VideoTile';

/**
 * Screen share video element
 */
const ScreenShareTile = ({ peerId }) => {
  const screenShareTrack = useHMSStore(selectScreenShareByPeerID(peerId));
  const { videoRef } = useVideo({
    trackId: screenShareTrack?.id
  });

  if (!screenShareTrack) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-gray-800">
        <p className="text-gray-400">Screen share loading...</p>
      </div>
    );
  }

  return (
    <video
      ref={videoRef}
      autoPlay
      playsInline
      className="w-full h-full object-contain bg-black"
    />
  );
};

/**
 * Screen Share View - displays screen share prominently with participant strip
 */
const ScreenShareView = ({ peers, localPeer }) => {
  const screenSharingPeer = useHMSStore(selectPeerScreenSharing);

  if (!screenSharingPeer) {
    return null;
  }

  // Get the presenter's name
  const presenterName = screenSharingPeer.name || 'Someone';
  const isLocalSharing = screenSharingPeer.id === localPeer?.id;

  return (
    <div className="flex flex-col h-full">
      {/* Presenter banner */}
      <div className="bg-gray-800 px-4 py-2 flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <span className="px-2 py-1 bg-green-600 text-white text-xs rounded">
            Screen Sharing
          </span>
          <span className="text-white text-sm">
            {isLocalSharing ? "You're presenting" : `${presenterName} is presenting`}
          </span>
        </div>
      </div>

      {/* Main content area */}
      <div className="flex-1 flex">
        {/* Screen share - takes most of the space */}
        <div className="flex-1 bg-black relative">
          <ScreenShareTile peerId={screenSharingPeer.id} />
        </div>

        {/* Participant strip on the right */}
        <div className="w-48 lg:w-64 bg-gray-900 flex flex-col overflow-y-auto">
          <div className="p-2 text-gray-400 text-xs uppercase tracking-wide border-b border-gray-700">
            Participants ({peers.length})
          </div>
          <div className="flex-1 overflow-y-auto p-2 space-y-2">
            {peers.map((peer) => (
              <div key={peer.id} className="aspect-video">
                <VideoTile peer={peer} isLocal={peer.id === localPeer?.id} />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScreenShareView;
